import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Trash2, CheckCircle } from 'lucide-react';
import MainLayout from '../../Components/Layout/MainLayout/MainLayout';
import { Button } from '../../Components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter
} from '../../Components/ui/dialog';

const Settings = () => {
  const { tenantDomain } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [tenant, setTenant] = useState({ tenantName: '', tenantDomain: '' });
  const [editedTenant, setEditedTenant] = useState({ tenantName: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [stripeStatus, setStripeStatus] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const stripeParam = searchParams.get('stripe');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [tenantRes, stripeRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/tenant`, {
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          }),
          fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/stripe/connect`, {
            credentials: 'include',
          }),
        ]);
        if (!tenantRes.ok) throw new Error(`HTTP error! Status: ${tenantRes.status}`);
        const data = await tenantRes.json();
        setTenant(data);
        setEditedTenant({ tenantName: data.tenantName });
        if (stripeRes.ok) setStripeStatus(await stripeRes.json());
      } catch (err) {
        setError('Failed to load settings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tenantDomain]);

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/stripe/connect`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error();
      const { url } = await res.json();
      window.location.href = url;
    } catch {
      setError('Failed to start Stripe onboarding. Please try again.');
      setConnectingStripe(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedTenant({ tenantName: tenant.tenantName });
    setError(null);
  };

  const handleSave = async () => {
    if (!editedTenant.tenantName.trim()) {
      setError('Tenant name cannot be empty');
      return;
    }
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/tenant`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tenantName: editedTenant.tenantName }),
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      setTenant({ ...tenant, tenantName: editedTenant.tenantName });
      setIsEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update tenant information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (deleteConfirmation !== tenant.tenantDomain) {
      setDeleteError(`Please type "${tenant.tenantDomain}" to confirm`);
      return;
    }
    try {
      setIsDeleting(true);
      setDeleteError(null);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tenant/${tenantDomain}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete tenant');
      navigate('/install');
    } catch (err) {
      setDeleteError(err.message || 'An error occurred while deleting the tenant');
      setIsDeleting(false);
    }
  };

  const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

  return (
    <MainLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your tenant configuration</p>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground py-8">Loading...</div>
      ) : (
        <div className="flex flex-col gap-10 max-w-2xl">

          {/* Tenant Information */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold">Tenant Information</h2>
              {!isEditing ? (
                <div className="flex items-center gap-3">
                  {success && <span className="text-xs text-green-600">Saved</span>}
                  <Button size="sm" variant="outline" onClick={() => { setIsEditing(true); setSuccess(false); }}>Edit</Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>Cancel</Button>
                  <Button size="sm" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </div>
            <hr className="mb-6" />
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tenant Domain</span>
                <span className="text-sm">{tenant.tenantDomain}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tenant Name</span>
                {isEditing ? (
                  <input
                    className={inputClass}
                    type="text"
                    name="tenantName"
                    value={editedTenant.tenantName}
                    onChange={(e) => setEditedTenant({ tenantName: e.target.value })}
                  />
                ) : (
                  <span className="text-sm">{tenant.tenantName}</span>
                )}
              </div>
            </div>
          </div>

          {/* Payments */}
          <div>
            <h2 className="text-sm font-semibold mb-3">Payments</h2>
            <hr className="mb-6" />

            {stripeParam === 'success' && (
              <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                Stripe onboarding complete. Your account is connected.
              </div>
            )}
            {stripeParam === 'refresh' && (
              <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-700">
                Your onboarding link expired. Click below to get a new one.
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Stripe Connect</p>
                {stripeStatus?.stripeOnboardingStatus === 'complete' ? (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Account connected — clients can pay invoices online.
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Connect your Stripe account to accept online payments.
                  </p>
                )}
              </div>
              {stripeStatus?.stripeOnboardingStatus === 'complete' ? (
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-green-100 text-green-700">
                  <CheckCircle size={13} /> Connected
                </span>
              ) : (
                <Button
                  size="sm"
                  onClick={handleConnectStripe}
                  disabled={connectingStripe}
                >
                  {connectingStripe
                    ? 'Redirecting...'
                    : stripeStatus?.stripeOnboardingStatus === 'pending'
                    ? 'Continue Onboarding'
                    : 'Connect Stripe'}
                </Button>
              )}
            </div>
          </div>

          {/* Danger Zone */}
          <div>
            <h2 className="text-sm font-semibold text-destructive mb-3">Danger Zone</h2>
            <hr className="border-destructive/20 mb-6" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Delete Tenant</p>
                <p className="text-sm text-muted-foreground mt-0.5">Permanently delete this tenant and all associated data.</p>
              </div>
              <Button
                variant="outline"
                onClick={() => { setShowDeleteModal(true); setDeleteConfirmation(''); setDeleteError(null); }}
                className="border-destructive/50 text-destructive hover:bg-destructive/10 shrink-0 ml-8"
              >
                <Trash2 size={14} className="mr-1.5" />
                Delete Tenant
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Tenant</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive mb-4">
              <p className="font-medium mb-1">This action cannot be undone.</p>
              <ul className="list-disc list-inside space-y-0.5 text-destructive/80">
                <li>All user roles for this tenant will be removed</li>
                <li>All tenant-user associations will be removed</li>
                <li>The tenant itself will be deleted</li>
              </ul>
              <p className="mt-2 text-xs">User accounts will not be deleted but will lose access.</p>
            </div>

            {deleteError && (
              <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
                {deleteError}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-sm">
                Type <strong>{tenant.tenantDomain}</strong> to confirm:
              </label>
              <input
                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                disabled={isDeleting}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTenant}
              disabled={isDeleting || deleteConfirmation !== tenant.tenantDomain}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete Tenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Settings;
